import axios from "axios";
import { notifyError } from "common/utils/app-monitoring/notify";
import { dropEmptyKeys } from "../core";
import { isSessionExpired, refreshCsrfToken } from "../csrf-token-manager";
import getMetaEnv from "../meta";
import { asyncRetryIfTransientError, fetchWithRetry } from "../retries";
import { withCache } from "./cache";
import { isAxiosNetworkError, shouldReportError } from "./error-classification";

export { isAxiosNetworkError, shouldReportError };

const withCsrfRetry = async (requestFn, source) => {
  try {
    return await requestFn();
  } catch (error) {
    if (!isSessionExpired(error)) throw error;

    const refreshed = await refreshCsrfToken(source);
    if (!refreshed) {
      throw error;
    }

    return requestFn();
  }
};

const AXIOS_DEFAULT_TIMEOUT = 15_000;

const MUTATION_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

const defaultHeaders = () => ({
  "X-CSRF-Token": getMetaEnv("csrf-token"),
  "X-Requested-With": "XMLHttpRequest",
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8"
});

const buildParams = ({ headers = {}, ...rest }, method) => ({
  method,
  headers: dropEmptyKeys({
    ...defaultHeaders(),
    ...headers
  }),
  credentials: "same-origin",
  ...rest
});

const haltErrorResponse = async response => {
  if (response.status >= 400 && response.status <= 599) {
    let errorMessage;
    let responseData;
    const textBody = await response.text();

    // Try to parse JSON response body
    try {
      responseData = JSON.parse(textBody);
    } catch {
      responseData = textBody;
    }

    if (textBody) {
      errorMessage =
        response.status === 422
          ? textBody
          : `${response.statusText || String(response.status)}: ${textBody}`;
    } else {
      errorMessage = response.statusText || String(response.status);
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    error.message = errorMessage;
    error.response = responseData; // Preserve the parsed response data
    throw error;
  }
  return response;
};

const increaseAsyncRequestsCount = () => {
  window.ajax_active += 1;
};

const decreaseAsyncRequestsCount = () => {
  window.ajax_active -= 1;
};

const sendBeacon = (url, data) =>
  new Promise((resolve, reject) => {
    const dataString = JSON.stringify(data);
    const queued = navigator.sendBeacon(url, dataString);

    if (queued) {
      resolve();
    } else {
      reject(new Error(`Failed to queue data for sending to ${url}`));
    }
  });

const executeFetch = (url, params, method) =>
  fetchWithRetry(url, buildParams(params, method))
    .then(haltErrorResponse)
    .then(async response => {
      if (response.status === 204) return undefined;
      const text = await response.text();
      if (!text) return undefined;
      try {
        return JSON.parse(text);
      } catch (parseError) {
        const error = new Error(`Non-JSON response for ${method} ${url}: ${text.slice(0, 80)}`);
        error.status = response.status;
        error.response = text;
        error.cause = parseError;
        throw error;
      }
    });

// `silent: true` opt-in skips notifyError() for known-noisy probes/polls
// (e.g. DTF artwork enhancement acknowledgement, jiffystickers composition
// lookup). The error is still rethrown so caller .catch handlers run; only
// the Honeybadger notify pipe is suppressed. See HB-1851.
const request = (url, params = {}, method = "GET") => {
  const { silent, ...rest } = params;
  increaseAsyncRequestsCount();

  if (rest.sendBeacon) {
    delete rest.sendBeacon;
    decreaseAsyncRequestsCount();
    return sendBeacon(url, rest);
  }

  const fetchPromise = MUTATION_METHODS.includes(method.toUpperCase())
    ? withCsrfRetry(() => executeFetch(url, rest, method), "request_layer")
    : executeFetch(url, rest, method);

  return fetchPromise
    .catch(error => {
      try {
        if (!silent && shouldReportError(error)) {
          notifyError(error);
        }
      } catch (err) {
        if (!silent) notifyError("Error has malformed data");
      }
      // Throw a network error only when there is no HTTP status code (mirrors
      // the guard in shouldReportError to avoid swallowing server error bodies).
      const httpStatus = error.status ?? error.response?.status;
      if (!httpStatus && isAxiosNetworkError(error)) {
        throw new Error("Unable to connect to server");
      }
      throw error;
    })
    .finally(decreaseAsyncRequestsCount);
};

/**
 * A non-native implementation of request method
 * You should use default export unless you need specific functions like progress tracking
 * @param url
 * @param params
 * @param method
 * @returns {Promise<void>|Promise<unknown>}
 */
const executeAxios = (url, params, method) => {
  const { timeout, ...axiosParams } = buildParams(params, method);
  return axios(url, {
    ...axiosParams,
    timeout: timeout ?? AXIOS_DEFAULT_TIMEOUT
  })
    .then(haltErrorResponse)
    .then(response => response.data);
};

export const axiosRequest = (url, params = {}, method = "GET") => {
  increaseAsyncRequestsCount();
  const { noRetry, silent, ...requestParams } = params;

  if (requestParams.sendBeacon) {
    delete requestParams.sendBeacon;
    decreaseAsyncRequestsCount();
    return sendBeacon(url, requestParams);
  }

  const upperMethod = method.toUpperCase();
  const isGet = upperMethod === "GET";
  const execute = () =>
    MUTATION_METHODS.includes(upperMethod)
      ? withCsrfRetry(() => executeAxios(url, requestParams, method), "axios_request_layer")
      : executeAxios(url, requestParams, method);

  const shouldRetry = isGet && !noRetry;
  const axiosPromise = shouldRetry
    ? asyncRetryIfTransientError(execute, { errorMatch: isAxiosNetworkError })
    : execute();

  return axiosPromise
    .catch(error => {
      try {
        if (!silent && shouldReportError(error)) {
          notifyError(error);
        }
      } catch (err) {
        if (!silent) notifyError("Error has malformed data");
      }
      throw error;
    })
    .finally(decreaseAsyncRequestsCount);
};

export default withCache(request);
